import { useCallback, useEffect, useState } from 'react';
import { useStoreActions, useStoreState } from '../../../store';
import { Button, Card, DatePicker, Descriptions, Form, Row, Select, Space, Spin, Typography } from 'antd';
import StyledCodeMirror from '../../../components/shared/StyledCodeMirror';
import { format } from 'date-fns/format';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { useForm } from 'antd/lib/form/Form';
import moment from 'moment-timezone';

const { Item } = Descriptions;
const { Text } = Typography;

export const assetToIdMap: any = {
  contacts: 'contactId',
  origins: 'originId',
  addresses: 'addressId',
  schedules: 'scheduleId',
  returnsSettings: 'returnsSettingsId',
  carrierAccounts: 'carrierAccountId',
  carrierAccountsV2: 'carrierAccountId',
  carrierSettings: 'carrierSettingsId',
  carrierRates: 'carrierRateId',
  carrierRateSets: 'carrierRateSetId',
  contracts: 'contractId',
  labelProperties: 'labelPropertiesId',
  labelPropertiesV2: 'labelPropertiesId',
  labelAugmentations: 'labelAugmentationId',
  augmentationSets: 'augmentationSetId',
  fulfillmentContexts: 'fulfillmentContextId',
  tenants: 'tenantId',
  carrierSelectionOverrides: 'selectionModifierId',
  thirdPartyBillingAccounts: 'thirdPartyBillingAccountId',
  thirdPartyBillingAccountSets: 'thirdPartyBillingAccountSetId',
  tags: 'tagId',
  packaging: 'packagingId',
  packagingSets: 'packagingSetId',
  injectionSite: 'injectionSiteId',
  injectionProfile: 'injectionProfileId',
};

export const assetToNameMap: any = {
  contacts: 'Contacts',
  origins: 'Origins',
  addresses: 'Addresses',
  schedules: 'Schedules',
  returnsSettings: 'Returns Settings',
  carrierAccounts: 'Carrier Accounts',
  carrierAccountsV2: 'Carrier Accounts V2',
  carrierSettings: 'Carrier Settings',
  carrierRates: 'Carrier Rates',
  carrierRateSets: 'Carrier Rate Sets',
  contracts: 'Contracts',
  labelProperties: 'Label Properties',
  labelPropertiesV2: 'Label Properties V2',
  labelAugmentations: 'Label Augmentations',
  augmentationSets: 'Augmentation Sets',
  fulfillmentContexts: 'Fulfillment Contexts',
  tenants: 'Tenants',
  carrierSelectionOverrides: 'Carrier Selection Overrides',
  thirdPartyBillingAccounts: 'Third Party Billing Accounts',
  thirdPartyBillingAccountSets: 'Third Party Billing Account Sets',
  tags: 'Tags',
  packaging: 'Packaging',
  packagingSets: 'Packaging Sets',
  injectionSite: 'Injection Site',
  injectionProfile: 'Injection Profile',
  limitsV2: 'Limits V2',
  packagingV2Set: 'Packaging Sets V2',
  rules: 'Rules',
  packagingV2: 'Packaging V2',
  shipOptions: 'Ship Options',
  rulesets: 'Rulesets',
  lanes: 'Shipment Lanes',
  laneSortConfigurations: 'Shipment Lane Sort Configurations',
  returnToAddressOverrides: 'Return To Address Overrides',
  costAdjustConfig: 'Cost Adjust Config',
  dockDoors: 'Dock Doors',
  serviceMethod: 'Service Method',
};

const AssetLedgerPage = () => {
  const ledgerEntries = useStoreState((state) => state.ledgerModel.ledgerEntries);
  const fetchLedgerEntries = useStoreActions((actions) => actions.ledgerModel.fetchLedgerEntries);
  const fetchLedgerEntriesByAsset = useStoreActions((actions) => actions.ledgerModel.fetchLedgerEntriesByAsset);
  const users = useStoreState((state) => state.users.users);
  const usersLength = Object.keys(users).length;
  const fetchUsers = useStoreActions((actions) => actions.users.fetchUsers);

  const availableAssetTypes = [...new Set(ledgerEntries.map((entry) => entry.assetType))]
    .map((entry) => {
      return {
        label: assetToNameMap[entry] || entry,
        value: entry,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  //handle query params
  const [query, setQuery] = useSearchParams();
  const [form] = useForm();

  const type = query.get('type');
  const assetId = query.get('asset-id');
  const date = query.get('date');

  useEffect(() => {
    form.setFieldsValue({
      assetId,
      type,
      date,
    });
  }, [assetId, type, date, form]);

  useEffect(() => {
    setLoading(true);
    fetchLedgerEntries({ asOfDateTime: date ? date : undefined });
    setLoading(false);
  }, [fetchLedgerEntries, date]);

  useEffect(() => {
    if (usersLength === 0) {
      fetchUsers(undefined);
    }
  }, [usersLength, fetchUsers]);

  const [currentLedgerEntries, setCurrentLedgerEntries] = useState(ledgerEntries);
  const [currentAssets, setCurrentAssets] = useState<any[]>([]);

  useEffect(() => {
    setCurrentLedgerEntries(ledgerEntries);
  }, [ledgerEntries]);

  const [showJson, setShowJson] = useState(new Set());

  const showHideJson = (entryId: string) => {
    if (showJson.has(entryId)) {
      const showing = new Set(showJson);
      showing.delete(entryId);
      setShowJson(showing);
    } else {
      const showing = new Set(showJson);
      showing.add(entryId);
      setShowJson(showing);
    }
  };

  const ledgerEntryElements = currentLedgerEntries.map((entry) => {
    return (
      <Card type="inner" key={entry.ledgerEntryId}>
        <Descriptions column={1}>
          <Item label={<b>Ledger ID</b>}>
            <Text copyable={{ text: entry.ledgerEntryId, tooltips: ['Copy Ledger ID', 'Copied'] }}>
              {entry.ledgerEntryId}
            </Text>
          </Item>
          <Item label={<b>Asset Type</b>}>{entry.assetType}</Item>
          <Item label={<b>Resource ID</b>}>
            <Text copyable={{ text: entry.assetId, tooltips: ['Copy Resource ID', 'Copied'] }}>{entry.assetId}</Text>
          </Item>
          <Item label={<b>Modified By</b>}>
            {users[entry.userId] ? `${users[entry.userId].givenName} ${users[entry.userId].surname}` : 'N/A'}
          </Item>
          <Item label={<b>Modified Timestamp</b>}>
            {(entry.timeStamp && format(new Date(entry.timeStamp), 'PPPP')) || 'N/A'}
          </Item>
        </Descriptions>
        <Button onClick={() => showHideJson(entry.ledgerEntryId)}>
          {showJson.has(entry.ledgerEntryId) ? 'Hide JSON' : 'Show JSON'}
        </Button>
        {showJson.has(entry.ledgerEntryId) && (
          <StyledCodeMirror
            title={`Change In JSON`}
            initialValue={entry.objectDelta}
            readOnly={true}
            mode={'javascript'}
            full={true}
          />
        )}
      </Card>
    );
  });

  const [loading, setLoading] = useState(false);

  const handleAssetTypeActionChange = (value: string) => {
    const newParams = new URLSearchParams(query);
    if (!value) {
      newParams.delete('type');
    } else newParams.set('type', value);
    newParams.delete('asset-id');
    setQuery(newParams, { replace: true });
  };

  const handleAssetActionChange = (value: string) => {
    const newParams = new URLSearchParams(query);
    if (!value) newParams.delete('asset-id');
    else newParams.set('asset-id', value);
    setQuery(newParams, { replace: true });
  };

  const handleDateActionChange = (value: any) => {
    const newParams = new URLSearchParams(query);
    if (!value) newParams.delete('date');
    else newParams.set('date', moment(value).format());
    setQuery(newParams, { replace: true });
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    if (type && assetId) {
      const res = await fetchLedgerEntriesByAsset({ assetType: type, assetId, asOfDateTime: date ? date : undefined });
      setCurrentLedgerEntries(res);
    } else if (type) {
      const entries = ledgerEntries.filter((entry) => entry.assetType === type);
      setCurrentLedgerEntries(entries);
      const assets = entries.map((asset) => {
        return {
          label: JSON.parse(asset.objectDelta).name,
          value: asset.assetId,
        };
      });
      const namedAssets = assets.filter((asset) => !!asset.label);
      const uniqeAssets = namedAssets.filter(
        (obj, index) => namedAssets.findIndex((item) => item.value === obj.value) === index,
      );
      setCurrentAssets(uniqeAssets);
    } else setCurrentLedgerEntries(ledgerEntries);
    setLoading(false);
  }, [assetId, fetchLedgerEntriesByAsset, ledgerEntries, type, date]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  return (
    <>
      <Card loading={false} style={{ marginBottom: 5 }} bodyStyle={{ paddingBottom: 0 }}>
        <Form layout={'horizontal'} form={form}>
          <Row>
            <Space>
              <Form.Item name="date" label="Choose Time" tooltip={'When should you search by?'} valuePropName={'date'}>
                <DatePicker showTime onChange={handleDateActionChange} />
              </Form.Item>
              <Form.Item name="type" label="Type" tooltip={'Which asset do you want to look for?'}>
                <Select
                  style={{ width: 300 }}
                  onChange={handleAssetTypeActionChange}
                  options={availableAssetTypes}
                  allowClear
                />
              </Form.Item>
              <Form.Item name="assetId" label="Asset" tooltip={'Pick an asset'}>
                <Select
                  style={{ width: 300 }}
                  allowClear
                  disabled={!type}
                  onChange={handleAssetActionChange}
                  options={currentAssets}
                />
              </Form.Item>
              <Button onClick={() => handleSearch()} hidden style={{ marginBottom: 23 }}>
                Search
              </Button>
            </Space>
          </Row>
        </Form>
        <Spin spinning={loading}>
          <Space direction="vertical">{...ledgerEntryElements}</Space>
        </Spin>
      </Card>
    </>
  );
};
export default AssetLedgerPage;
